import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialFields = {
  email: "",
  firstName: "",
  lastName: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
  cardNumber: "",
  expiry: "",
  cvc: "",
  cardName: "",
};

function CheckOut() {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = subtotal >= siteConfig.shipping.freeThreshold ? 0 : siteConfig.shipping.standardCost;
  const total = subtotal + shipping;

  const setField = (name, value) => {
    setFields((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const formatCard = (value) =>
    value
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(.{4})/g, "$1 ")
      .trim();

  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
  };

  const validate = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(fields.email)) next.email = "Enter a valid email address";
    ["firstName", "lastName", "address", "city", "state", "postalCode", "country", "cardName"].forEach(
      (name) => {
        if (!fields[name].trim()) next[name] = "This field is required";
      }
    );
    if (fields.cardNumber.replace(/\D/g, "").length !== 16) next.cardNumber = "Enter a 16-digit card number";
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(fields.expiry)) next.expiry = "Use MM/YY";
    if (fields.cvc.length < 3) next.cvc = "Enter your security code";
    return next;
  };

  const placeOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      window.requestAnimationFrame(() => {
        document.querySelector(".checkout-field.has-error input, .checkout-field.has-error select")?.focus();
      });
      return;
    }

    setSubmitting(true);
    const orderNumber = `${siteConfig.orderPrefix}-${Date.now().toString().slice(-8)}`;
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: { orderNumber, total, email: fields.email },
      });
    }, 700);
  };

  if (!lines.length) {
    return (
      <main className="checkout-empty">
        <h1>Your bag is empty.</h1>
        <p>Add a piece to your bag before continuing to checkout.</p>
        <Link className="button button--dark" to="/shop">Return to the collection</Link>
      </main>
    );
  }

  const shippingFields = [
    { name: "firstName", label: "First name", autoComplete: "given-name" },
    { name: "lastName", label: "Last name", autoComplete: "family-name" },
    { name: "address", label: "Address", autoComplete: "street-address", wide: true },
    { name: "apartment", label: "Apartment, suite, etc. (optional)", autoComplete: "address-line2", wide: true },
    { name: "city", label: "City", autoComplete: "address-level2" },
    { name: "state", label: "State", autoComplete: "address-level1" },
    { name: "postalCode", label: "ZIP code", autoComplete: "postal-code" },
  ];

  return (
    <main className="checkout-page">
      <section className="checkout-form-column">
        <Link className="checkout-back" to="/cartpage"><FiArrowLeft /> Back to bag</Link>
        <div className="checkout-title">
          <p className="eyebrow">CHECKOUT</p>
          <h1>Complete your order</h1>
        </div>

        <form id="checkout-form" onSubmit={placeOrder} noValidate>
          <section className="checkout-section">
            <div className="checkout-section__heading">
              <span>01</span>
              <h2>Contact</h2>
            </div>
            <label className={`checkout-field checkout-field--wide ${errors.email ? "has-error" : ""}`}>
              <span>Email address</span>
              <input
                type="email"
                value={fields.email}
                onChange={(event) => setField("email", event.target.value)}
                autoComplete="email"
              />
              {errors.email && <small>{errors.email}</small>}
            </label>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__heading">
              <span>02</span>
              <h2>Delivery address</h2>
            </div>
            <div className="checkout-fields">
              <label className={`checkout-field checkout-field--wide ${errors.country ? "has-error" : ""}`}>
                <span>Country / region</span>
                <select value={fields.country} onChange={(event) => setField("country", event.target.value)}>
                  <option>United States</option>
                  <option>Canada</option>
                  <option>United Kingdom</option>
                  <option>France</option>
                  <option>Australia</option>
                </select>
                {errors.country && <small>{errors.country}</small>}
              </label>
              {shippingFields.map((field) => (
                <label
                  key={field.name}
                  className={`checkout-field ${field.wide ? "checkout-field--wide" : ""} ${errors[field.name] ? "has-error" : ""}`}
                >
                  <span>{field.label}</span>
                  <input
                    value={fields[field.name]}
                    onChange={(event) => setField(field.name, event.target.value)}
                    autoComplete={field.autoComplete}
                  />
                  {errors[field.name] && <small>{errors[field.name]}</small>}
                </label>
              ))}
            </div>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__heading">
              <span>03</span>
              <h2>Payment</h2>
              <small><FiLock /> Encrypted and secure</small>
            </div>
            <div className="payment-box">
              <div className="payment-box__title">
                <span>Credit or debit card</span>
                <span>VISA · MC · AMEX</span>
              </div>
              <div className="checkout-fields">
                <label className={`checkout-field checkout-field--wide ${errors.cardNumber ? "has-error" : ""}`}>
                  <span>Card number</span>
                  <input
                    inputMode="numeric"
                    value={fields.cardNumber}
                    onChange={(event) => setField("cardNumber", formatCard(event.target.value))}
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                  />
                  {errors.cardNumber && <small>{errors.cardNumber}</small>}
                </label>
                <label className={`checkout-field ${errors.expiry ? "has-error" : ""}`}>
                  <span>Expiration</span>
                  <input
                    inputMode="numeric"
                    value={fields.expiry}
                    onChange={(event) => setField("expiry", formatExpiry(event.target.value))}
                    autoComplete="cc-exp"
                    placeholder="MM/YY"
                  />
                  {errors.expiry && <small>{errors.expiry}</small>}
                </label>
                <label className={`checkout-field ${errors.cvc ? "has-error" : ""}`}>
                  <span>Security code</span>
                  <input
                    inputMode="numeric"
                    value={fields.cvc}
                    onChange={(event) => setField("cvc", event.target.value.replace(/\D/g, "").slice(0, 4))}
                    autoComplete="cc-csc"
                    placeholder="CVC"
                  />
                  {errors.cvc && <small>{errors.cvc}</small>}
                </label>
                <label className={`checkout-field checkout-field--wide ${errors.cardName ? "has-error" : ""}`}>
                  <span>Name on card</span>
                  <input
                    value={fields.cardName}
                    onChange={(event) => setField("cardName", event.target.value)}
                    autoComplete="cc-name"
                  />
                  {errors.cardName && <small>{errors.cardName}</small>}
                </label>
              </div>
            </div>
          </section>
        </form>
      </section>

      <aside className="checkout-summary">
        <div className="checkout-summary__inner">
          <h2>Your order</h2>
          <div className="checkout-summary__items">
            {lines.map(({ product, quantity, size, color }) => (
              <div className="checkout-item" key={`${product.id}-${size}-${color}`}>
                <div className="checkout-item__image">
                  <img src={product.image} alt="" />
                  <span>{quantity}</span>
                </div>
                <div>
                  <h3>{product.name}</h3>
                  <p>{color} · {size}</p>
                </div>
                <span>{formatPrice(product.price * quantity)}</span>
              </div>
            ))}
          </div>
          <div className="checkout-totals">
            <div><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
            <div><span>Delivery</span><span>{shipping ? formatPrice(shipping) : "Complimentary"}</span></div>
            <div className="checkout-totals__grand"><span>Total</span><strong>{formatPrice(total)}</strong></div>
          </div>
          <button className="button button--dark" type="submit" form="checkout-form" disabled={submitting}>
            {submitting ? "Processing…" : `Pay ${formatPrice(total)}`}
          </button>
          <p className="checkout-consent">
            By placing your order, you agree to our <Link to="/terms">terms</Link> and acknowledge our <Link to="/privacy">privacy policy</Link>.
          </p>
        </div>
      </aside>
    </main>
  );
}

export default CheckOut;
